/**
 * Role-based route guard for the admin app.
 * Decodes the JWT token to check the user's role before rendering.
 */

import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import config from './config';

interface RoleGuardProps {
  allowedRoles: string[];
  children?: React.ReactNode;
}

/**
 * Decodes a JWT token payload without verifying signature (client-side only).
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

/**
 * Get the current user's role from the JWT cookie.
 */
export function getUserRole(): string | null {
  const token = Cookies.get(config.tokenCookieName);
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  return (payload?.role as string) ?? null;
}

/**
 * Get the current user's ID from the JWT cookie.
 */
export function getUserId(): number | null {
  const token = Cookies.get(config.tokenCookieName);
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  return (payload?.sub as number) ?? null;
}

/**
 * Get the lab name from the JWT cookie.
 */
export function getLabName(): string | null {
  const token = Cookies.get(config.tokenCookieName);
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  return (payload?.lab_name as string) ?? null;
}

/**
 * Get user profile info from the JWT cookie.
 */
export function getUserProfile(): {
  id: number | null;
  email: string | null;
  role: string | null;
  lab_name: string | null;
  first_name: string | null;
  last_name: string | null;
} {
  const token = Cookies.get(config.tokenCookieName);
  if (!token) return { id: null, email: null, role: null, lab_name: null, first_name: null, last_name: null };
  const payload = decodeJwtPayload(token);
  if (!payload) return { id: null, email: null, role: null, lab_name: null, first_name: null, last_name: null };
  return {
    id: (payload.sub as number) ?? null,
    email: (payload.email as string) ?? null,
    role: (payload.role as string) ?? null,
    lab_name: (payload.lab_name as string) ?? null,
    first_name: (payload.first_name as string) ?? null,
    last_name: (payload.last_name as string) ?? null,
  };
}

/**
 * Route guard component that restricts access based on JWT role.
 * Redirects to /signin if not authenticated, or /dashboard if wrong role.
 */
const RoleGuard: React.FC<RoleGuardProps> = ({ allowedRoles, children }) => {
  const location = useLocation();
  const token = Cookies.get(config.tokenCookieName);

  if (!token) {
    return <Navigate to="/signin" replace state={{ from: location }} />;
  }

  const role = getUserRole();
  if (!role || !allowedRoles.includes(role)) {
    // Already on the fallback route: redirecting again would loop.
    if (location.pathname === '/dashboard') {
      return null;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
};

export default RoleGuard;
